import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { ISetContentControlledPermissionsParams, SetContentControlledPermissionsRequest } from '~permissions/requests/SetContentControlledPermissionsRequest';
import { WebClientDetailFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { FieldValuesQuery } from '~src/httpClient/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '~src/httpClient/resources/Resource';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { GetFieldValuesRequest } from '~src/httpClient/serverApi/requests/GetFieldValuesRequest';
import { IActionResult } from '~src/httpClient/serverApi/types/IActionResult';
import { IEmptyResult, IMultipleContentTypeResult, IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { ISetProjectControlledPermissionsParams, SetProjectControlledPermissionsRequest } from '~src/permissions/requests/SetProjectControlledPermissionsRequest';
import { Sanitizer } from '~src/projects/helpers/Sanitizer';
import {
  GetProjectAncestorsByLuidRequest,
  IGetProjectAncestorsByLuidResult
} from '~src/projects/ServerApi/requests/GetProjectAncestorsByLuidRequest';
import { ContainerType, ContentItem, MoveContentToContainerRequest } from '~src/projects/ServerApi/requests/MoveContentToContainerRequest';
import { INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';

import { IProjectDetailInfo, IProjectInfo } from '../models/IProjectInfo';
import { ProjectMapper } from '../models/mappers/ProjectMapper';
import { CreateProjectRequest, IProjectIdResult } from '../ServerApi/requests/CreateProjectRequest';
import { DeleteProjectsRequest } from '../ServerApi/requests/DeleteProjectsRequest';
import { GetProjectActionsRequest } from '../ServerApi/requests/GetProjectActionsRequest';
import { GetProjectAncestorsRequest, IGetProjectAncestorsResult } from '../ServerApi/requests/GetProjectAncestorsRequest';
import { GetProjectsRequest } from '../ServerApi/requests/GetProjectsRequest';
import { MoveProjectsToProjectRequest } from '../ServerApi/requests/MoveProjectsToProjectRequest';
import { SetProjectDescriptionRequest } from '../ServerApi/requests/SetProjectDescriptionRequest';
import { SetProjectNameRequest } from '../ServerApi/requests/SetProjectNameRequest';
import { SetProjectsOwnerRequest } from '../ServerApi/requests/SetProjectsOwnerRequest';
import { IProjectActions } from '../ServerApi/types/IProjectActions';

export class ProjectsResource implements IListResource<IProjectInfo>, IFieldValuesResource, IDetailResource<IProjectDetailInfo>,
    IActionResource<IProjectActions> {
  private listFetcher = new WebClientListResourceFetcher(GetProjectsRequest, ServerApiMapper.projects);
  private detailFetcher = new WebClientDetailFetcher('getProject', ProjectMapper.mapDetail);
  private fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);

  public all(): ResourceQuery<IProjectInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string): Promise<IProjectDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'project');
  }

  // Actions

  public actions(id: string): Promise<IProjectActions> {
    return WebHttpClient.sendRequest(new GetProjectActionsRequest(id));
  }

  public del(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequestsAndDelay(
        RequestHelper.buildRequests(ids, (idsBlock) => new DeleteProjectsRequest(idsBlock)),
        ResultHelper.combineResults
      );
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetProjectNameRequest(id, name));
  }

  public setDescription(id: string, description: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetProjectDescriptionRequest(id, description));
  }

  public setProjectControlledPermissions(params: ISetProjectControlledPermissionsParams): Promise<IEmptyResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequest(new SetProjectControlledPermissionsRequest(params));
  }

  public setContentControlledPermissions(params: ISetContentControlledPermissionsParams): Promise<IActionResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequest(new SetContentControlledPermissionsRequest(params));
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequestsAndDelay(
        RequestHelper.buildRequests(ids, (idsBlock) => new SetProjectsOwnerRequest(idsBlock, ownerId)),
        ResultHelper.combineResults
      );
  }

  /**
   * Returns all the ancestors in the nested project chain from the top level,
   * including the project with the given ID.
   */
  public getProjectHierarchy(id: string): Promise<INamedItemInfo[]> {
    return WebHttpClientWithErrorToastInstance
      .sendRequest(new GetProjectAncestorsRequest(id))
      .then((result: IGetProjectAncestorsResult) => result.projects ? result.projects : [])
      .catch(_ => []);
  }

  public getProjectHierarchyByLuid(luid: string): Promise<INamedItemInfo[]> {
    return WebHttpClientWithErrorToastInstance
      .sendRequest(new GetProjectAncestorsByLuidRequest(luid))
      .then((result: IGetProjectAncestorsByLuidResult) => result.projects ? result.projects : [])
      .catch(_ => []);
  }

  public moveToProject(targetProjectId: string, projectIds: string[]): Promise<IResult> {
    const requests = RequestHelper.buildRequests(
      projectIds,
      idsBlock => new MoveProjectsToProjectRequest({ ids: idsBlock, projectId: targetProjectId })
    );
    return WebHttpClientWithErrorToastInstance
      .sendRequestsAndDelay(requests, ResultHelper.combineResults);
  }

  public create(name: string, description?: string, parentProjectId?: string): Promise<IProjectIdResult> {
    const request = new CreateProjectRequest({
      name: name,
      description: Sanitizer.trimTextOrUndefined(description),
      parentProjectId: parentProjectId
    });
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(request);
  }

  public moveToContainer(containerId: string, containerType: string, contentItems: ContentItem[]): Promise<IMultipleContentTypeResult> {

    const requests = RequestHelper.buildRequests(
      contentItems,
      contentItemsBlock => new MoveContentToContainerRequest({
        containerId: containerId,
        containerType: ContainerType[containerType as keyof typeof ContainerType],
        contentItems: contentItemsBlock})
    );
    return WebHttpClientWithErrorToastInstance
      .sendRequestsAndDelay(requests, ResultHelper.combineMultipleContentTypeResults);
  }
}

export const Projects = new ProjectsResource();
